import { ChangeEvent, FC, memo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ChipSelectorControlled } from 'components/_basic/Form/ChipSelector/ChipSelectorControlled';
import { RadioControlled } from 'components/_basic/Form/Radio';
import { Responsive } from 'components/_basic/Responsive';
import { PROJECT_STATUS_OPTIONS_DEU } from 'components/BusinessToolForm/constants/deu/options_deu';
import { PROJECT_STATUS_OPTIONS_ES } from 'components/BusinessToolForm/constants/es/options_es';
import { PROJECT_STATUS_OPTIONS } from 'components/BusinessToolForm/constants/options';
import { PROJECT_STATUS_OPTIONS_UA } from 'components/BusinessToolForm/constants/ua/options_ua';
import {
  COMPANY_PROJECT_NAMES,
  prefix,
} from 'components/BusinessToolForm/Sections/Overview/ProjectOverview/utils/form';

import { concatWithDot } from 'helpers/string';

import { ISharedProps } from '../utils/types';

import styles from './ProjectStatus.module.scss';

export const ProjectStatus: FC<ISharedProps> = memo(({ canAccess }) => {
  const { i18n } = useTranslation();
  const { control } = useFormContext();
  const name = concatWithDot(prefix, COMPANY_PROJECT_NAMES.STATUS);
  const id = 'overview.project.projectStatus';

  const { field } = useController({
    name,
    control,
  });
  const getChosenOptions = (lang: string) => {
    switch (lang) {
      case 'de':
        return PROJECT_STATUS_OPTIONS_DEU;

      case 'uk':
        return PROJECT_STATUS_OPTIONS_UA;

      case 'es':
        return PROJECT_STATUS_OPTIONS_ES;

      default:
        return PROJECT_STATUS_OPTIONS;
    }
  };

  const onChange = (_: ChangeEvent<HTMLInputElement>, value: string) => {
    const valueAsObject = getChosenOptions(i18n.language).find(
      (option) => option.value.toLowerCase() === value.toLowerCase(),
    );
    if (valueAsObject) {
      field.onChange([valueAsObject]);
    }
  };

  return (
    <>
      <Responsive mobile>
        <RadioControlled
          name={name}
          id={id}
          disabled={!canAccess}
          options={getChosenOptions(i18n.language)}
          radioControlledStyles={{ radioLabel: styles.radioLabel }}
          onChange={onChange}
        />
      </Responsive>
      <Responsive aboveMobile>
        <ChipSelectorControlled
          control={control}
          id={id}
          options={getChosenOptions(i18n.language)}
          name={name}
          disabled={!canAccess}
        />
      </Responsive>
    </>
  );
});
